import {
  Settings,
  Lock,
  Unlock,
  Shield,
  Camera,
  Keyboard,
  Eye,
  Captions,
  Image,
  PictureInPicture,
  Maximize2,
  Minimize2,
  MoreVertical,
  Cloud,
  Download,
  Circle,
  Video,
  VideoOff,
  Volume2,
  VolumeX,
  Loader2,
  PhoneOutgoing,
  Phone,
} from "lucide-react";
import { cn } from "@vc/lib/utils";
import { ControlButton } from "../common/ControlButton";
import { TrackedDropdownMenu } from "../common/TrackedDropdownMenu";
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/clientWeb/components/ui/dropdown-menu";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/clientWeb/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectWrapper } from "@/clientWeb/components/ui/select";
import { Button } from "@/clientWeb/components/ui/button";
import { Input } from "@/clientWeb/components/ui/input";
import { Label } from "@/clientWeb/components/ui/label";
import { toast } from "@/clientWeb/components/ui/toast-utils";
import { dialOut, cancelDialOut, listDialOut, SipDialError, type DialProtocol, type DialOutCall } from "@/features/meeting/services/sipDial.api";

interface MoreOptionsMenuProps {
  fullscreenOn?: boolean;
  captionsOn?: boolean;
  roomLocked?: boolean;
  onOpenSettings?: () => void;
  onToggleLock?: () => void;
  onOpenHostControls?: () => void;
  onOpenAddVideoInput?: () => void;
  onOpenShortcuts?: () => void;
  onAdjustView?: () => void;
  onToggleCaptions?: () => void;
  /** This user's own cloud recording is running. */
  cloudRecordingOn?: boolean;
  /** A cloud stop is in flight: the item spins and stops responding until
   *  the mixer confirms. */
  cloudRecordingStopping?: boolean;
  onStopCloudRecording?: () => void;
  onStartCloudRecording?: () => void;
  /** This user's own local (browser) recording is running. */
  localRecordingOn?: boolean;
  onStopLocalRecording?: () => void;
  onStartLocalRecording?: () => void;
  onOpenBackgroundEffects?: () => void;
  onTogglePictureInPicture?: () => void;
  onToggleFullscreen?: () => void;
  isMyVideoPaused?: boolean;
  areIncomingVideosPaused?: boolean
  /** Any remote camera is on. Without one there is nothing to pause. */
  anyRemoteCameraOn?: boolean;
  onPauseMyVideo?: () => void;
  onResumeMyVideo?: () => void;
  onToggleIncomingVideosPause?: () => void;
  areIncomingAudiosPaused?: boolean;
  /** Any remote mic is on. Without one there is nothing to pause. */
  anyRemoteMicOn?: boolean;
  onToggleIncomingAudiosPause?: () => void;
}

export function MoreOptionsMenu({
  fullscreenOn,
  captionsOn,
  roomLocked,
  onOpenSettings,
  onToggleLock,
  onOpenHostControls,
  onOpenAddVideoInput,
  onOpenShortcuts,
  onAdjustView,
  onToggleCaptions,
  cloudRecordingOn,
  cloudRecordingStopping,
  onStopCloudRecording,
  onStartCloudRecording,
  localRecordingOn,
  onStopLocalRecording,
  onStartLocalRecording,
  onOpenBackgroundEffects,
  onTogglePictureInPicture,
  onToggleFullscreen,
  areIncomingVideosPaused,
  anyRemoteCameraOn = false,
  onToggleIncomingVideosPause,
  areIncomingAudiosPaused,
  anyRemoteMicOn = false,
  onToggleIncomingAudiosPause,
}: MoreOptionsMenuProps) {
  // The dialog lives here rather than in MeetingScreen: it holds no state the
  // rest of the meeting needs, so threading an open/close prop through
  // ControlBar would add two files' worth of plumbing for nothing.
  const [dialOutOpen, setDialOutOpen] = useState(false);
  const [callee, setCallee] = useState("");
  // SIP by default, as it has always been. The caller picks H.323 explicitly
  // when they want it — the backend can resolve a protocol from the Address
  // Book, but guessing on the caller's behalf is not what this dialog does.
  const [protocol, setProtocol] = useState<DialProtocol>("sip");
  const [transport, setTransport] = useState("udp");
  const [bitrate, setBitrate] = useState("1024");
  const [dialing, setDialing] = useState(false);
  // The call placed by this dialog, so its progress can be shown and cancelled.
  const [call, setCall] = useState<DialOutCall | null>(null);
  const { meetingId } = useParams<{ meetingId: string }>();
  const t = useTranslation();

  /**
   * Place the call.
   *
   * Same path the Participants panel already uses: the browser never talks to
   * the SIP gateway, it posts to the backend proxy, which holds the gateway key
   * and resolves the meeting to its room UUID. `callee` is sent exactly as
   * typed — a full SIP URI, or a bare name the gateway resolves against its
   * registry.
   *
   * Protocol IS sent now (stage H7) — it used to be collected and discarded, so
   * every dial went out as SIP whatever the dropdown said. Transport and bitrate
   * are still not sent: the dial APIs take no such parameters, so sending them
   * would be a silent no-op.
   *
   * The dialog stays OPEN after a successful dial: an H.323 call reports
   * ringing → connected, and closing immediately would hide the one thing the
   * caller wants to see. It also leaves Cancel reachable.
   */
  const handleDialOut = async () => {
    const to = callee.trim();
    if (!to || !meetingId || dialing) return;
    setDialing(true);
    setCall(null);
    try {
      const placed = await dialOut(meetingId, to, protocol);
      setCall(placed);
      toast.success({
        title: "Calling",
        description: `Dialing ${to} over ${placed.protocol === "h323" ? "H.323" : "SIP"}.`,
      });
      // SIP returns no call record to follow, so there is nothing to show.
      if (!placed.callId) {
        setCallee("");
        setDialOutOpen(false);
      }
    } catch (e) {
      toast.error({
        title: "Dial failed",
        description: e instanceof SipDialError ? e.message : "Could not place the call.",
      });
    } finally {
      setDialing(false);
    }
  };
  // Follow the call while it is still ringing. The gateway also pushes
  // h323:dialout_status to the room, but the control bar has no subscription of
  // its own — polling a single call for a few seconds is far less plumbing than
  // threading a room event down here, and it stops the moment the call settles.
  useEffect(() => {
    if (!meetingId || !call?.callId) return;
    if (call.status && !["dialing", "ringing"].includes(call.status)) return;

    const id = setInterval(async () => {
      try {
        const { calls } = await listDialOut(meetingId);
        const mine = calls.find((c) => c.callId === call.callId);
        if (mine) setCall((prev) => (prev?.callId === mine.callId ? { ...prev, ...mine } : prev));
      } catch {
        /* a failed poll is not worth showing; the next one may succeed */
      }
    }, 2000);

    return () => clearInterval(id);
  }, [meetingId, call?.callId, call?.status]);

  const cancelInFlight = async () => {
    if (!meetingId || !call?.callId) return;
    try {
      const cancelled = await cancelDialOut(meetingId, call.callId);
      setCall((prev) => (prev ? { ...prev, ...cancelled } : cancelled));
    } catch (e) {
      toast.error({
        title: "Could not cancel",
        description: e instanceof SipDialError ? e.message : "The call may have already ended.",
      });
    }
  };

  const ringing = call?.status === "dialing" || call?.status === "ringing";

  return (
    <TrackedDropdownMenu>
      <DropdownMenuTrigger asChild>
        <div>
          <ControlButton onClick={() => { }} label={t("controlBar.more")}>
            <MoreVertical />
          </ControlButton>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {onOpenSettings && (
          <DropdownMenuItem
            onSelect={() => {
              onOpenSettings();
            }}
          >
            <Settings className="mr-2 h-4 w-4" />
            {t("controlBar.settings")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={() => {
            setDialOutOpen(true);
          }}
        >
          <PhoneOutgoing className="mr-2 h-4 w-4" />
          {t("controlBar.dialOut")}
        </DropdownMenuItem>
        {onToggleLock && (
          <DropdownMenuItem
            onSelect={() => {
              onToggleLock();
            }}
          >
            {roomLocked ? (
              <Unlock className="mr-2 h-4 w-4" />
            ) : (
              <Lock className="mr-2 h-4 w-4" />
            )}
            {roomLocked ? t("controlBar.unlockRoom") : t("controlBar.lockRoom")}
          </DropdownMenuItem>
        )}
        {onOpenHostControls && (
          <DropdownMenuItem
            onSelect={() => {
              onOpenHostControls();
            }}
          >
            <Shield className="mr-2 h-4 w-4" />
            {t("controlBar.hostControls")}
          </DropdownMenuItem>
        )}

        {/* Recording Options: cloud and local are independent, each with its
            own Start/Stop item so stopping one never touches the other. */}
        {onStartCloudRecording && !cloudRecordingOn && (
          <DropdownMenuItem
            onSelect={() => {
              onStartCloudRecording();
            }}
          >
            <Cloud className="mr-2 h-4 w-4" />
            {t("controlBar.startCloudRecording")}
          </DropdownMenuItem>
        )}
        {cloudRecordingOn && onStopCloudRecording && (
          <DropdownMenuItem
            disabled={cloudRecordingStopping}
            onSelect={() => {
              if (cloudRecordingStopping) return;
              onStopCloudRecording();
            }}
            className="text-red-600"
          >
            {cloudRecordingStopping ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Circle className="mr-2 h-4 w-4 fill-current" />
            )}
            {cloudRecordingStopping ? t("recordingStatus.stopping") : t("controlBar.stopCloudRecording")}
          </DropdownMenuItem>
        )}
        {onStartLocalRecording && !localRecordingOn && (
          <DropdownMenuItem
            onSelect={() => {
              onStartLocalRecording();
            }}
          >
            <Download className="mr-2 h-4 w-4" />
            {t("controlBar.startLocalRecording")}
          </DropdownMenuItem>
        )}
        {localRecordingOn && onStopLocalRecording && (
          <DropdownMenuItem
            onSelect={() => {
              onStopLocalRecording();
            }}
            className="text-red-600"
          >
            <Circle className="mr-2 h-4 w-4 fill-current" />
            {t("controlBar.stopLocalRecording")}
          </DropdownMenuItem>
        )}

        {onOpenAddVideoInput && (
          <DropdownMenuItem
            onSelect={() => {
              onOpenAddVideoInput();
            }}
          >
            <Camera className="mr-2 h-4 w-4" />
            {t("controlBar.addNewVideoInput")}
          </DropdownMenuItem>
        )}
        {onOpenShortcuts && (
          <DropdownMenuItem
            onSelect={() => {
              onOpenShortcuts();
            }}
          >
            <Keyboard className="mr-2 h-4 w-4" />
            {t("controlBar.keyboardShortcuts")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          onSelect={() => {
            onAdjustView?.();
          }}
        >
          <Eye className="mr-2 h-4 w-4" />
          {t("controlBar.layouts")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            onToggleCaptions?.();
          }}
        >
          <Captions
            className={cn("mr-2 h-4 w-4", captionsOn ? "text-green-600" : "text-amber-600")}
          />
          {captionsOn ? t("controlBar.turnOffCaptions") : t("controlBar.turnOnCaptions")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            onOpenBackgroundEffects?.();
          }}
        >
          <Image className="mr-2 h-4 w-4" />
          {t("controlBar.applyBackgroundEffects")}
        </DropdownMenuItem>
        {/* Rendered only when a handler is supplied. Previously this entry was
            unconditional with an optional-chained call, so on every screen that
            passed no handler it appeared in the menu and silently did nothing. */}
        {onTogglePictureInPicture && (
          <DropdownMenuItem
            onSelect={() => {
              onTogglePictureInPicture();
            }}
          >
            <PictureInPicture className="mr-2 h-4 w-4" />
            {t("controlBar.pictureInPicture")}
          </DropdownMenuItem>
        )}
        {/* <DropdownMenuItem
          onSelect={() => {
            if (isMyVideoPaused) {
              onResumeMyVideo?.()
              return
            }
            onPauseMyVideo?.()
          }}
        >
          {isMyVideoPaused ? <Video className="mr-2 h-4 w-4" /> : <VideoOff className="mr-2 h-4 w-4" />}
          {isMyVideoPaused ? "Resume My Video" : "Pause My Video"}
        </DropdownMenuItem> */}
        {/* Disabled with no remote camera on: there is nothing to pause, and
            offering the action anyway told a host looking at a wall of avatars
            that there were videos to stop. Stays available while paused —
            that is the only way back. */}
        <DropdownMenuItem
          disabled={!anyRemoteCameraOn && !areIncomingVideosPaused}
          onSelect={() => {
            onToggleIncomingVideosPause?.()
          }}
        >
          {areIncomingVideosPaused ? <Video className="mr-2 h-4 w-4" /> : <VideoOff className="mr-2 h-4 w-4" />}
          {areIncomingVideosPaused ? t("controlBar.resumeAllVideos") : t("controlBar.pauseAllIncomingVideos")}
        </DropdownMenuItem>
        {/* The audio twin, same rule: nothing to pause with every remote mic
            off, and always available while paused. Share audio is exempt. */}
        <DropdownMenuItem
          disabled={!anyRemoteMicOn && !areIncomingAudiosPaused}
          onSelect={() => {
            onToggleIncomingAudiosPause?.()
          }}
        >
          {areIncomingAudiosPaused ? <Volume2 className="mr-2 h-4 w-4" /> : <VolumeX className="mr-2 h-4 w-4" />}
          {areIncomingAudiosPaused ? t("controlBar.resumeAllAudio") : t("controlBar.pauseAllIncomingAudio")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            // Let the menu finish closing before the full-screen transition.
            //
            // `onSelect` runs while Radix is still tearing this layer down, and
            // `requestFullscreen` sets off a transition that re-lays out the
            // page and fires `fullscreenchange` — which closes panels and
            // dispatches a resize. Doing that on top of a half-dismissed layer
            // is what leaves the page unclickable. Waiting a frame keeps the two
            // apart. The menu still closes by itself — preventing the default
            // here would hold it open, which is not what we want.
            requestAnimationFrame(() => onToggleFullscreen?.());
          }}
        >
          {fullscreenOn ? (
            <>
              <Minimize2 className="mr-2 h-4 w-4" />
              {t("controlBar.exitFullScreen")}
            </>
          ) : (
            <>
              <Maximize2 className="mr-2 h-4 w-4" />
              {t("controlBar.fullScreen")}
            </>
          )}
        </DropdownMenuItem>
        {/* Only while there is a full screen to escape from. This was
            rendered unconditionally, so opening the menu in a normal window
            told the user to press Esc to leave a mode they were not in — and
            Esc there does nothing, so following the instruction taught them the
            menu was lying. `fullscreenOn` was already a prop; nothing but the
            condition was missing.

            The separator goes with it. Left behind it would draw a divider
            under the last real item, which reads as a group with nothing in
            it. */}
        {fullscreenOn && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
              }}
            >
              {t("controlBar.pressEscToExit")}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>

      {/* Dial Out — call a SIP/H.323 endpoint into this meeting.
          Built from the same Dialog + SelectWrapper + Label/Input pieces the
          settings pages use, so it matches the rest of the product.
          CALL places a real call and sends the protocol; transport and bitrate
          remain presentational until the dial APIs accept them. */}
      <Dialog open={dialOutOpen} onOpenChange={setDialOutOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("controlBar.dialOut")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Callee */}
            <div className="space-y-2">
              <Label htmlFor="dialout-callee">Callee *</Label>
              <Input
                id="dialout-callee"
                value={callee}
                maxLength={128}
                onChange={(e) => setCallee(e.target.value)}
                placeholder="Enter name or number"
              />
            </div>

            {/* Protocol + transport, side by side as in the design */}
            <div className="grid grid-cols-2 gap-3">
              <SelectWrapper label="Protocol">
                <Select value={protocol} onValueChange={(v) => setProtocol(v as DialProtocol)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sip">SIP</SelectItem>
                    <SelectItem value="h323">H.323</SelectItem>
                  </SelectContent>
                </Select>
              </SelectWrapper>

              <SelectWrapper label="Transport">
                <Select value={transport} onValueChange={setTransport}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="udp">UDP</SelectItem>
                    <SelectItem value="tcp">TCP</SelectItem>
                    <SelectItem value="tls">TLS</SelectItem>
                  </SelectContent>
                </Select>
              </SelectWrapper>
            </div>

            {/* Bitrate */}
            <SelectWrapper label="Bitrate">
              <Select value={bitrate} onValueChange={setBitrate}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["512", "768", "1024", "1536", "2048", "4096"].map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SelectWrapper>
          </div>

          <div className="flex items-center justify-between gap-3 mt-6">
            {/* How the call is going. Only H.323 reports this — a SIP dial
                returns no call record to follow (its stage S2), and the dialog
                closes straight away in that case. */}
            <div className="min-w-0 text-sm">
              {call?.callId && (
                <span className="inline-flex items-center gap-2">
                  <span
                    className={
                      call.status === "connected"
                        ? "inline-block w-2 h-2 rounded-full bg-emerald-500"
                        : ringing
                          ? "inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse"
                          : "inline-block w-2 h-2 rounded-full bg-slate-400"
                    }
                  />
                  <span className="truncate text-slate-600 dark:text-slate-300">
                    {call.status === "ringing"
                      ? "Ringing…"
                      : call.status === "connected"
                        ? "Connected"
                        : call.status === "dialing"
                          ? "Dialing…"
                          : call.endReason || call.status}
                  </span>
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Cancel is only meaningful before the endpoint answers. */}
              {ringing && (
                <Button variant="outline" onClick={cancelInFlight}>
                  Cancel
                </Button>
              )}

              {/* Disabled until there is somebody to call, and while one is in flight. */}
              <Button
                className="gap-2"
                disabled={!callee.trim() || !meetingId || dialing}
                onClick={handleDialOut}
              >
                {dialing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Phone className="w-4 h-4" />
                )}
                {t("controlBar.call")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TrackedDropdownMenu>
  );
}
